import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SearchableSelect } from '../Shared/SearchableSelect';
import { monitoringService } from '../../services/data';
import './MonitoringRunPage.css';

const initialForm = {
  id_monitorizare: null,
  search_from: '',
  search_to: '',
};

const levelLabels = {
  critical: 'Critical',
  warning: 'Warning',
  moderate: 'Moderate',
};

const MonitoringRunPage = () => {
  const [options, setOptions] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [result, setResult] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [running, setRunning] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [retryingKeyword, setRetryingKeyword] = useState('');
  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  const clearPolling = () => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => () => clearPolling(), []);

  useEffect(() => {
    const loadOptions = async () => {
      setLoadingOptions(true);
      setError('');
      try {
        const data = await monitoringService.getOptions();
        setOptions(data || []);
      } catch (requestError) {
        setError(requestError.message || 'Nu am putut încărca lista de monitorizări active.');
      } finally {
        setLoadingOptions(false);
      }
    };

    loadOptions();
  }, []);

  const selectOptions = useMemo(
    () => options.map((item) => ({ value: item.id, label: item.marca || `Monitorizare #${item.id}` })),
    [options]
  );

  const handleRun = async (event) => {
    event.preventDefault();
    setError('');
    setResult(null);
    setSendingEmail(false);
    setRetryingKeyword('');
    clearPolling();

    if (!form.id_monitorizare || !form.search_from || !form.search_to) {
      setError('Selectează monitorizarea și intervalul de timp.');
      return;
    }

    setRunning(true);
    setProgress(0);
    setProgressMessage('Pregătim job-ul...');
    try {
      const startData = await monitoringService.runStart(form);
      const startedJobId = startData.job_id;
      setJobId(startedJobId);
      let finished = false;

      const pollJob = async () => {
        try {
          const statusData = await monitoringService.runStatus(startedJobId);
          setProgress(statusData.progress || 0);
          setProgressMessage(statusData.message || 'Monitoring in progress...');

          if (statusData.status === 'completed') {
            finished = true;
            setResult(statusData.result || null);
            setRunning(false);
            setProgress(100);
            setProgressMessage('Finalizat');
            clearPolling();
            return;
          }

          if (statusData.status === 'failed') {
            finished = true;
            setError(statusData.error || 'Rularea monitorizării a eșuat.');
            setRunning(false);
            setProgress(100);
            setProgressMessage('Eșec');
            clearPolling();
          }
        } catch (pollError) {
          finished = true;
          setError(pollError.response?.data?.detail || pollError.message || 'Nu am putut citi progresul.');
          setRunning(false);
          clearPolling();
        }
      };

      await pollJob();
      if (!finished) {
        pollRef.current = window.setInterval(pollJob, 1500);
      }
    } catch (requestError) {
      setError(requestError.response?.data?.detail || requestError.message || 'Rularea monitorizării a eșuat.');
      setProgress(0);
      setProgressMessage('');
    } finally {
      if (!pollRef.current) {
        setRunning(false);
      }
    }
  };

  const formatDate = (value) => {
    if (!value) {
      return '-';
    }

    if (value.length >= 10) {
      return value.slice(0, 10);
    }
    return value;
  };

  const handleSendEmail = async () => {
    if (!result?.result_id || sendingEmail || result?.email_sent) {
      return;
    }

    setSendingEmail(true);
    setError('');

    try {
      const data = await monitoringService.sendEmail(result.result_id);
      setResult((prev) => (prev ? {
        ...prev,
        email_sent: true,
        email_sent_date: data.email_sent_date || prev.email_sent_date,
        recipient_email: data.recipient || prev.recipient_email,
      } : prev));
    } catch (requestError) {
      setError(requestError.response?.data?.detail || requestError.message || 'Nu am putut trimite emailul.');
    } finally {
      setSendingEmail(false);
    }
  };

  const getKeywordError = (keywordData) => (keywordData?.error || keywordData?.partial_error ? 'TMView error' : '');

  const getKeywordResultsLabel = (keywordData) => {
    if (getKeywordError(keywordData)) {
      return 'TMView error';
    }
    if (typeof keywordData?.total_results === 'number') {
      return `${keywordData.total_results} results`;
    }
    return '0 results';
  };

  const hasKeywordErrors = Object.values(result?.keywords_results || {}).some((keywordData) => Boolean(getKeywordError(keywordData)));

  const handleRetryKeyword = async (keyword) => {
    if (!result?.result_id || !keyword || running || retryingKeyword) {
      return;
    }

    setError('');
    setRetryingKeyword(keyword);
    try {
      const updatedResult = await monitoringService.retryKeyword(result.result_id, keyword);
      setResult(updatedResult || null);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || requestError.message || 'Nu am putut rerula keyword-ul.');
    } finally {
      setRetryingKeyword('');
    }
  };

  return (
    <div className="monitoring-run-page">
      <div className="monitoring-run-header">
        <p>Selectează o monitorizare și rulează verificarea pentru intervalul dorit.</p>
      </div>

      {error && <div className="monitoring-alert monitoring-alert-error">{error}</div>}

      <form className="monitoring-run-form" onSubmit={handleRun}>
        <div className="monitoring-run-grid">
          <div className="monitoring-field monitoring-field-wide">
            <label>Trademark / Brand</label>
            <SearchableSelect
              options={selectOptions}
              value={form.id_monitorizare}
              onChange={(value) => setForm((prev) => ({ ...prev, id_monitorizare: value }))}
              placeholder="Caută monitorizare activă..."
            />
          </div>

          <div className="monitoring-field">
            <label>From</label>
            <input
              type="date"
              value={form.search_from}
              onChange={(event) => setForm((prev) => ({ ...prev, search_from: event.target.value }))}
              required
            />
          </div>

          <div className="monitoring-field">
            <label>To</label>
            <input
              type="date"
              value={form.search_to}
              onChange={(event) => setForm((prev) => ({ ...prev, search_to: event.target.value }))}
              required
            />
          </div>
        </div>

        <div className="monitoring-run-actions">
          <button type="submit" className="btn-primary" disabled={running || loadingOptions}>
            {running ? 'Running...' : 'Run Monitoring'}
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              clearPolling();
              setForm(initialForm);
              setResult(null);
              setRunning(false);
              setSendingEmail(false);
              setRetryingKeyword('');
              setJobId(null);
              setProgress(0);
              setProgressMessage('');
              setError('');
            }}
          >
            Clear
          </button>
        </div>

        {running && (
          <div className="monitoring-progress">
            <div className="monitoring-progress-head">
              <span>{progressMessage || 'Monitoring in progress...'}</span>
              <strong>{progress}%</strong>
            </div>
            <div className="monitoring-progress-track">
              <div className="monitoring-progress-fill" style={{ width: `${progress}%` }} />
            </div>
            {jobId && <small className="monitoring-job-id">Job: {jobId}</small>}
          </div>
        )}
      </form>

      {loadingOptions && <div className="monitoring-alert">Se încarcă monitorizările active...</div>}

      {result && (
        <div className="monitoring-results">
          {result.success && result.result_id && (
            <div className="monitoring-results-actions">
              <button
                type="button"
                className={`btn-primary monitoring-email-button${result.email_sent ? ' is-sent' : ''}`}
                onClick={handleSendEmail}
                disabled={sendingEmail || result.email_sent}
              >
                {sendingEmail ? 'Sending...' : result.email_sent ? 'Email sent' : 'Send email'}
              </button>

              {result.email_sent && (
                <span className="monitoring-email-meta">
                  {result.recipient_email ? `Trimis la ${result.recipient_email}` : 'Trimis cu succes'}
                </span>
              )}
            </div>
          )}

          <div className="monitoring-summary-grid">
            <div className="monitoring-summary-card">
              <strong>Period</strong>
              <span>{result.search_period?.from} → {result.search_period?.to}</span>
            </div>
            <div className="monitoring-summary-card">
              <strong>Total Matches</strong>
              <span>{result.total_matches ?? 0}</span>
            </div>
            <div className="monitoring-summary-card">
              <strong>Status</strong>
              <span>{result.success ? 'Success' : 'Error'}</span>
            </div>
            <div className="monitoring-summary-card">
              <strong>Generated</strong>
              <span>{formatDate(result.timestamp)}</span>
            </div>
          </div>

          {!result.total_matches && !hasKeywordErrors && (
            <div className="monitoring-alert">No trademarks found for this period.</div>
          )}

          {Object.entries(result.keywords_results || {}).map(([keyword, keywordData]) => (
            <div key={keyword} className="keyword-block">
              <div className="keyword-header">
                <h3>
                  Keyword: <strong>{keyword}</strong> ({getKeywordResultsLabel(keywordData)})
                </h3>

                {getKeywordError(keywordData) && (
                  <button
                    type="button"
                    className="keyword-retry-button"
                    onClick={() => handleRetryKeyword(keyword)}
                    disabled={retryingKeyword === keyword || !!retryingKeyword || running}
                    title="Retry keyword"
                  >
                    <span className={`retry-icon ${retryingKeyword === keyword ? 'is-spinning' : ''}`} aria-hidden="true">↻</span>
                    <span>{retryingKeyword === keyword ? 'Retrying...' : 'Retry'}</span>
                  </button>
                )}
              </div>

              {getKeywordError(keywordData) && (
                <div className="monitoring-alert monitoring-alert-error">{getKeywordError(keywordData)}</div>
              )}

              {['critical', 'warning', 'moderate'].map((level) => {
                const trademarks = keywordData.grouped?.[level] || [];
                if (!trademarks.length) {
                  return null;
                }

                return (
                  <div key={level} className="level-block">
                    <div className={`level-badge ${level}`}>
                      {levelLabels[level]} ({trademarks.length})
                    </div>

                    <div className="monitoring-table-wrap">
                      <table className="monitoring-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Office</th>
                            <th>Applicant(s)</th>
                            <th>Nice Classes</th>
                            <th>Application Date</th>
                            <th>Similarity %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {trademarks.map((trademark, index) => {
                            const data = trademark.data || {};
                            const applicants = Array.isArray(data.applicantName) ? data.applicantName.join(', ') : '-';
                            const classes = Array.isArray(data.niceClass) ? data.niceClass.join(', ') : '-';

                            return (
                              <tr key={`${level}-${index}`}>
                                <td>{data.tmName || '-'}</td>
                                <td>{data.tmOffice || '-'}</td>
                                <td>{applicants || '-'}</td>
                                <td>{classes || '-'}</td>
                                <td>{formatDate(data.applicationDate)}</td>
                                <td>{typeof trademark.similarity_percent === 'number' ? trademark.similarity_percent.toFixed(2) : '-'}%</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MonitoringRunPage;
